import { useNavigate } from 'react-router-dom';
import { Card, Stack, useTheme, spacing, fontSize } from '@fti/design-system';
import { useUser } from '../../auth/hooks';

const QUICK_LINKS = [
  { label: 'Projects', path: '/simulations/projects', desc: 'Create and manage media simulation projects' },
  { label: 'Dashboard', path: '/simulations/dashboard', desc: 'Review activity and simulation metrics' },
  { label: 'Explore Publications', path: '/publications', desc: 'Browse publications and reflection profiles' },
  { label: 'Request Publications', path: '/publications/request', desc: 'Request new publications to be added' },
];

export function HomePage() {
  const { theme } = useTheme();
  const navigate = useNavigate();
  const user = useUser();

  const textColor = theme.colors.text.primary;
  const mutedColor = theme.colors.text.secondary;
  const primaryColor = theme.colors.primary[700];

  const greetingName = user?.email ? user.email.split('@')[0] : user?.name || '';

  const bodyStyle = {
    fontSize: '0.9rem',
    color: mutedColor,
    margin: 0,
    lineHeight: 1.7,
  };

  return (
    <Stack gap="xl">
      {/* Greeting */}
      <div>
        <h1 style={{ color: primaryColor, margin: 0, fontSize: '2rem', fontWeight: 700 }}>
          Welcome{greetingName ? `, ${greetingName}` : ''}
        </h1>
        <p style={{ fontSize: '1rem', color: mutedColor, margin: `${spacing.xs} 0 0` }}>
          Simulate media coverage. Sharpen messaging. Shape outcomes.
        </p>
      </div>

      {/* Navigation tiles */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
        gap: spacing.md,
      }}>
        {QUICK_LINKS.map((link) => (
          <Card
            key={link.path}
            variant="elevated"
            style={{ cursor: 'pointer', transition: 'transform 0.1s, box-shadow 0.1s' }}
            onClick={() => navigate(link.path)}
          >
            <Card.Body>
              <h3 style={{ margin: 0, fontWeight: 600, fontSize: '1rem', color: textColor }}>
                {link.label}
              </h3>
              <p style={{ margin: 0, marginTop: spacing.xs, fontSize: fontSize.sm, color: mutedColor }}>
                {link.desc}
              </p>
            </Card.Body>
          </Card>
        ))}
      </div>

      {/* About */}
      <Stack gap="md">
        <h2 style={{ color: primaryColor, margin: 0, fontSize: '1.2rem', fontWeight: 600 }}>
          What This Tool Does
        </h2>
        <p style={bodyStyle}>
          A primary vehicle for reputational management is through traditional media
          coverage. In moments of change, scrutiny, and opportunity, traditional media
          plays a critical role in shaping how our clients are perceived by the public.
          As a result, a core function of the Strategic Communications practice is to
          advise clients on what, how, when, and to whom they should communicate to
          influence coverage and further a desired outcome.
        </p>
        <p style={bodyStyle}>
          FTI's Media Simulation takes advantage of generative AI and historical news
          data to simulate media coverage. Media agents, each trained to adopt the
          perspective of a specific publication, are asked to "write" a news article in
          response to a press release, announcement, or general context. Analysis of
          this simulated news coverage enables FTI to improve messaging recommendations,
          enhance media strategy, test messaging, and guide client expectations.
        </p>
      </Stack>

      <Stack gap="md">
        <h2 style={{ color: primaryColor, margin: 0, fontSize: '1.2rem', fontWeight: 600 }}>
          How It Works
        </h2>
        <p style={bodyStyle}>
          The simulation begins by studying how each selected publication actually
          writes. The system analyzes hundreds of real articles from a publication's
          archive to build a detailed profile of its editorial style &mdash; how it
          structures stories, what kind of language it uses in headlines, how it
          attributes quotes, what tone it takes, and how it frames different types of
          events. These profiles, called reflection notes, act as a style guide that
          ensures each simulated article feels authentic to its publication.
        </p>
        <p style={bodyStyle}>
          Once these profiles are built, the system generates articles through a
          three-stage editorial process that mirrors a real newsroom. First, a
          journalist agent plans an editorial angle and writes a full article using the
          publication's style, your provided messaging, and any additional context
          about the event. Next, a headline writer crafts a headline that matches the
          publication's conventions. Finally, an editor agent reviews the complete
          article and headline for quality &mdash; checking that the style, tone,
          structure, and sourcing are consistent with how that publication actually
          operates. Articles that don't meet the standard are rejected, just as they
          would be in a real editorial process.
        </p>
        <p style={bodyStyle}>
          The result is a set of simulated articles across multiple publications, each
          reflecting how that outlet would realistically cover a client's announcement. You
          can review accepted and rejected articles, compare coverage across
          publications, and use these insights to refine messaging before it reaches
          the real press.
        </p>
      </Stack>
    </Stack>
  );
}
